/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable sonarjs/cognitive-complexity */
'use client';

import {
  Box,
  Card,
  Image,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  rem,
} from '@mantine/core';
import { useLocale, useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import type { SubscriptionDetail } from '../api';
import SubscriptionFooter from './subcription-footer';
import SubscriptionBenefit from './subscription-benefit';
import styles from './subscription-styles.module.css';
import { assetPrefix } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';

type SubscriptionDetailContentProps = {
  data: SubscriptionDetail;
};

export default function SubscriptionDetailContent({
  data,
}: SubscriptionDetailContentProps) {
  const tSubscription = useTranslations('subscription');
  const locale = useLocale();
  const isMobile = useIsMobile();
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(
    data.plans[0].id
  );

  const selectedPlan = useMemo(() => {
    if (!selectedPlanId) return null;
    return data.plans.find((plan) => plan.id === selectedPlanId);
  }, [selectedPlanId, data]);

  if (!data) return null;

  return (
    <>
      <ScrollArea
        flex={1}
        type="never"
        w="100%"
        h="100%"
        pos="relative"
        pb={0}
        classNames={{
          viewport: '[&>div]:!block',
        }}
      >
        <Card pos="sticky" top={0} radius={0}>
          <Card.Section>
            <Image
              src={data.image_url}
              w="100%"
              h={isMobile ? 160 : 240}
              alt="banner subscription"
              fallbackSrc={`${assetPrefix}/static/images/sub-banner.webp`}
            />
          </Card.Section>
        </Card>

        <Card
          flex={1}
          top={isMobile ? -16 : -24}
          pt={isMobile ? rem(16) : rem(24)}
          pb={isMobile ? rem(16 + 128) : rem(24)}
          radius={isMobile ? rem(12) : rem(24)}
          style={{ zIndex: 2 }}
        >
          <Stack gap={isMobile ? rem(12) : rem(16)}>
            <Box>
              <Text
                lineClamp={2}
                size={isMobile ? 'sm' : 'md'}
                fw={500}
                c="neutral.7"
              >
                {data.description[locale]}
              </Text>
              {selectedPlan && (
                <Text c="neutral.6" size={isMobile ? 'xs' : 'sm'} fw={500}>
                  {data.is_auto_renew
                    ? tSubscription('auto_renew_after', {
                        days: selectedPlan?.life_time_in_days,
                      })
                    : tSubscription('expiry_in_date', {
                        days: selectedPlan.life_time_in_days,
                      })}
                </Text>
              )}
            </Box>

            {data.plans?.length > 1 ? (
              <Tabs
                variant="default"
                value={selectedPlanId}
                onChange={setSelectedPlanId}
              >
                <Tabs.List
                  classNames={{
                    list: styles.tabListDetails,
                  }}
                >
                  {data.plans.length > 0 &&
                    data.plans.map((plan) => (
                      <Tabs.Tab
                        c={
                          plan.id === selectedPlanId ? 'primary.6' : 'neutral.6'
                        }
                        px={rem(8)}
                        py={rem(12)}
                        w={`${100 / data.plans.length}%`}
                        value={plan.id}
                        key={plan.id}
                        style={
                          plan.id === selectedPlanId
                            ? {
                                borderBottom: '2px solid primary.6',
                              }
                            : {}
                        }
                        classNames={{
                          tab: styles.tabDetails,
                        }}
                      >
                        <Text
                          size="md"
                          fw={plan.id === selectedPlanId ? 600 : 500}
                        >
                          {tSubscription('days', {
                            days: plan.life_time_in_days,
                          })}
                        </Text>
                      </Tabs.Tab>
                    ))}
                </Tabs.List>
                {data.plans.length > 0 &&
                  data.plans.map((plan) => (
                    <Tabs.Panel
                      py={isMobile ? rem(12) : rem(16)}
                      key={plan.id}
                      value={plan.id}
                    >
                      <SubscriptionBenefit
                        details={data}
                        plan={plan}
                        termAndConditionUrl={data.term_and_condition_url}
                      />
                    </Tabs.Panel>
                  ))}
              </Tabs>
            ) : (
              <SubscriptionBenefit
                details={data}
                plan={data.plans[0]}
                termAndConditionUrl={data.term_and_condition_url}
              />
            )}
          </Stack>
        </Card>
      </ScrollArea>

      {selectedPlan && <SubscriptionFooter plan={selectedPlan} />}
    </>
  );
}
